import { getItemDef } from "../items/db.js";
import {
  aggregateWeaponStats,
  fittedMagazine,
  isTrueMagazine,
  magCountKnown,
  weaponWithMagRounds,
  weaponWithMod,
} from "../items/mods.js";
import type { ItemDef, ItemInstance, WeaponItemDef, WeaponSlotDef } from "../items/types.js";
import { findFreePlacement, type GridState, type Rotation } from "./grid.js";
import { cloneInventory, inventoryGrids, type Inventory } from "./state.js";

/**
 * Where the next magazine comes from. Reloading is two things at two times:
 * deciding WHAT to do (`planReload`, the instant R is pressed, both sides) and
 * doing it (`applyReloadPlan`, when the animation completes on the server, and
 * on the client to predict). Two kinds, decided by `WeaponItemDef.reloadStyle`:
 *
 *   SWAP   the default: the magazine comes out and the fullest spare goes in.
 *          Rounds never move — two instances trade places. Refused up front
 *          when the old box has nowhere to land.
 *   TOPUP  the shotgun: shells pushed into the fitted magazine. With rounds
 *          loaded, only stacks of the SAME cartridge; when empty, the first
 *          compatible stack in scan order decides what the magazine becomes.
 *
 * Rng-free; scan order is `inventoryGrids` order (pockets, rig, backpack),
 * then `items` order, TOP-LEVEL GRIDS ONLY. The server plans once on press and
 * again on completion (the magazine may have been dragged away), so
 * `applyReloadPlan` tolerates a stale plan and answers `null`.
 */

export interface ReloadTake {
  iid: string;
  /** Units to remove from that stack. Always >= 1. */
  count: number;
}

/**
 * What pressing R will do. `reloadSec` is on both arms so the server has one
 * field to start its clock from.
 */
export type ReloadPlan =
  | {
      kind: "swap";
      /** The spare magazine going IN. Always fuller than what is in the gun. */
      incomingIid: string;
      reloadSec: number;
    }
  | {
      kind: "topup";
      /** Stacks to consume, in the order they were found. */
      take: ReloadTake[];
      /** What the magazine will contain afterwards. */
      ammoDefId: string;
      /** Rounds actually added — may be fewer than the magazine can hold. */
      rounds: number;
      reloadSec: number;
    };

/** The gun's magazine well, or `null` for a weapon that has none. */
function magSlotOf(def: WeaponItemDef): WeaponSlotDef | null {
  for (const slot of def.slots ?? []) {
    if (slot.id === "mag") return slot;
  }
  return null;
}

/**
 * Plans a reload, or `null` when there is nothing to do. Touches neither weapon
 * nor inventory — `applyReloadPlan` commits.
 */
export function planReload(
  inv: Inventory,
  weapon: ItemInstance,
  weaponDef: WeaponItemDef,
): ReloadPlan | null {
  return weaponDef.reloadStyle === "topup"
    ? planTopUp(inv, weapon, weaponDef)
    : planSwap(inv, weapon, weaponDef);
}

/**
 * THE SWAP: the fullest compatible spare, ties by scan order, and proof the
 * old magazine has somewhere to go. An empty spare is never a candidate; the
 * spare need not be fuller than what is in the gun (a held R is stopped by
 * the server's edge trigger, not by this plan). `reloadSec` comes from the
 * weapon WITH THE INCOMING MAGAZINE fitted — the drum pays its seating time
 * going in.
 */
function planSwap(
  inv: Inventory,
  weapon: ItemInstance,
  weaponDef: WeaponItemDef,
): ReloadPlan | null {
  const slot = magSlotOf(weaponDef);
  if (slot === null) return null;

  let best: { item: ItemInstance; rounds: number } | null = null;
  for (const { grid } of inventoryGrids(inv)) {
    for (const placed of grid.items) {
      const item = placed.item;
      const def = getItemDef(item.defId);
      if (!isTrueMagazine(def)) continue;
      // The same whitelist `attach` applies: a drum the carbine would refuse is
      // not a magazine the carbine can reload with either.
      if (!slot.accepts.includes(def.id)) continue;
      const rounds = item.mag ?? 0;
      if (rounds <= 0) continue;
      // Strictly greater, so the first magazine found at a given count keeps it.
      if (best === null || rounds > best.rounds) best = { item, rounds };
    }
  }
  if (best === null) return null;

  // Pre-verify the eject against the inventory as it WILL be: the incoming
  // magazine's cell is a legitimate landing spot.
  const outgoing = weapon.mods?.["mag"];
  if (outgoing !== undefined) {
    const outDef = getItemDef(outgoing.defId);
    if (outDef === null) return null;
    const after = withoutTopLevel(inv, best.item.iid);
    if (firstFreeSpot(after, outDef) === null) return null;
  }

  const candidate = weaponWithMod(weapon, "mag", best.item);
  return {
    kind: "swap",
    incomingIid: best.item.iid,
    reloadSec: aggregateWeaponStats(weaponDef, candidate).reloadSec,
  };
}

/**
 * THE TOP-UP: shells from loose stacks into the magazine already in the gun.
 * Requires a fitted magazine (`null` otherwise); capacity, count and type come
 * off the MAGAZINE instance.
 */
function planTopUp(
  inv: Inventory,
  weapon: ItemInstance,
  weaponDef: WeaponItemDef,
): ReloadPlan | null {
  const fitted = fittedMagazine(weaponDef, weapon);
  if (fitted === null) return null;
  const current = fitted.item.mag ?? 0;
  const need = fitted.def.magCapacity - current;
  if (need <= 0) return null;

  // Rounds with no recorded type select as empty: the first compatible stack
  // decides.
  const loaded = current > 0 ? fitted.item.ammoDefId ?? null : null;

  const take: ReloadTake[] = [];
  let chosen: string | null = loaded;
  let rounds = 0;

  for (const { grid } of inventoryGrids(inv)) {
    for (const placed of grid.items) {
      if (rounds >= need) break;
      const item = placed.item;
      const def = getItemDef(item.defId);
      if (def === null || def.kind !== "ammo") continue;
      // The MAGAZINE's calibre, which `validateItemDb` proves is the gun's.
      if (def.caliber !== fitted.def.caliber) continue;
      if (chosen === null) chosen = item.defId;
      else if (item.defId !== chosen) continue;

      const want = need - rounds;
      const n = item.stack < want ? item.stack : want;
      if (n <= 0) continue;
      take.push({ iid: item.iid, count: n });
      rounds += n;
    }
    if (rounds >= need) break;
  }

  if (chosen === null || rounds === 0) return null;
  return {
    kind: "topup",
    take,
    ammoDefId: chosen,
    rounds,
    reloadSec: aggregateWeaponStats(weaponDef, weapon).reloadSec,
  };
}

/**
 * Commits a plan: the new inventory AND weapon instance, or `null` when the
 * plan can no longer be honoured (magazine dragged away, landing cell filled).
 * Pure. The weapon comes back as a replacement — this file does not know which
 * slot holds it.
 */
export function applyReloadPlan(
  inv: Inventory,
  weapon: ItemInstance,
  weaponDef: WeaponItemDef,
  plan: ReloadPlan,
): { inv: Inventory; weapon: ItemInstance } | null {
  if (plan.kind === "topup") {
    const fitted = fittedMagazine(weaponDef, weapon);
    if (fitted === null) return null;
    const have = fitted.item.mag ?? 0;
    return {
      inv: applyReloadTake(inv, plan.take),
      weapon: weaponWithMagRounds(weapon, have + plan.rounds, plan.ammoDefId),
    };
  }

  // --- THE SWAP: the incoming magazine leaves its cell FIRST so the outgoing
  // one may land in the space it freed — the common one-spare case.
  const next = cloneInventory(inv);
  const found = takeTopLevel(next, plan.incomingIid);
  if (found === null) return null;

  const outgoing = weapon.mods?.["mag"];
  const nextWeapon = weaponWithMod(weapon, "mag", found);

  if (outgoing !== undefined) {
    const outDef = getItemDef(outgoing.defId);
    if (outDef === null) return null;
    const spot = firstFreeSpot(next, outDef);
    if (spot === null) return null;
    spot.grid.items.push({ item: outgoing, x: spot.x, y: spot.y, rot: spot.rot });
  }
  return { inv: next, weapon: nextWeapon };
}

/** Removes a top-level item from `inv` IN PLACE and hands it back, or `null`.
 *  Only ever called on a clone. */
function takeTopLevel(inv: Inventory, iid: string): ItemInstance | null {
  for (const { grid } of inventoryGrids(inv)) {
    for (let i = 0; i < grid.items.length; i++) {
      const placed = grid.items[i] as { item: ItemInstance };
      if (placed.item.iid !== iid) continue;
      grid.items.splice(i, 1);
      return placed.item;
    }
  }
  return null;
}

/** A copy of `inv` with one top-level item gone — the "as it will be" state the
 *  eject check has to run against. Cheap: fresh grids, shared instances. */
function withoutTopLevel(inv: Inventory, iid: string): Inventory {
  const next = cloneInventory(inv);
  takeTopLevel(next, iid);
  return next;
}

/**
 * The first cell across the player's grids, in canonical order, that fits
 * `def`.
 */
function firstFreeSpot(
  inv: Inventory,
  def: ItemDef,
): { grid: GridState; x: number; y: number; rot: Rotation } | null {
  for (const { grid } of inventoryGrids(inv)) {
    const at = findFreePlacement(grid, def);
    if (at !== null) return { grid, x: at.x, y: at.y, rot: at.rot };
  }
  return null;
}

/**
 * Applies a top-up plan's `take` list, removing stacks that hit zero. Returns a
 * NEW inventory. Stacks no longer there are skipped rather than failing the
 * reload.
 */
export function applyReloadTake(inv: Inventory, take: readonly ReloadTake[]): Inventory {
  if (take.length === 0) return inv;

  const counts = new Map<string, number>();
  for (const t of take) counts.set(t.iid, (counts.get(t.iid) ?? 0) + t.count);

  const next = cloneInventory(inv);

  for (const { grid } of inventoryGrids(next)) {
    for (let i = grid.items.length - 1; i >= 0; i--) {
      const placed = grid.items[i] as { item: ItemInstance; x: number; y: number; rot: 0 | 1 };
      const want = counts.get(placed.item.iid);
      if (want === undefined) continue;
      const left = placed.item.stack - want;
      if (left > 0) {
        grid.items[i] = {
          item: { ...placed.item, stack: left },
          x: placed.x,
          y: placed.y,
          rot: placed.rot,
        };
      } else {
        grid.items.splice(i, 1);
      }
    }
  }
  return next;
}

/**
 * Total LOOSE rounds of `ammoDefId` in the player's grids — for filling
 * magazines by hand and topping the shotgun up, NOT what a swap can reach
 * (`countReserveRounds`).
 */
export function countAmmo(inv: Inventory, ammoDefId: string): number {
  let n = 0;
  for (const { grid } of inventoryGrids(inv)) {
    for (const placed of grid.items) {
      if (placed.item.defId === ammoDefId) n += placed.item.stack;
    }
  }
  return n;
}

/**
 * Rounds in COMPATIBLE SPARE MAGAZINES in the player's grids — the HUD's
 * reserve, since R spends a magazine, not a cartridge. Every magazine the `mag`
 * slot accepts, whatever is in it; top-level grids only.
 */
export function countReserveRounds(inv: Inventory, weaponDef: WeaponItemDef): number {
  const slot = magSlotOf(weaponDef);
  if (slot === null) return 0;
  let n = 0;
  for (const { grid } of inventoryGrids(inv)) {
    for (const placed of grid.items) {
      const def = getItemDef(placed.item.defId);
      if (!isTrueMagazine(def)) continue;
      if (!slot.accepts.includes(def.id)) continue;
      n += placed.item.mag ?? 0;
    }
  }
  return n;
}

/**
 * `countReserveRounds` as the player KNOWS it: rounds in spares whose count
 * they have (`magCountKnown`), plus how many spares they cannot number — the
 * HUD's "+?". `countReserveRounds` stays the true total a reload plans against.
 */
export function countReserveKnown(
  inv: Inventory,
  weaponDef: WeaponItemDef,
): { rounds: number; unknownMags: number } {
  const slot = magSlotOf(weaponDef);
  if (slot === null) return { rounds: 0, unknownMags: 0 };
  let rounds = 0;
  let unknownMags = 0;
  for (const { grid } of inventoryGrids(inv)) {
    for (const placed of grid.items) {
      const def = getItemDef(placed.item.defId);
      if (!isTrueMagazine(def)) continue;
      if (!slot.accepts.includes(def.id)) continue;
      if (magCountKnown(def, placed.item)) rounds += placed.item.mag ?? 0;
      else unknownMags += 1;
    }
  }
  return { rounds, unknownMags };
}
